import { MakeLogicType, actions, afterMount, kea, key, path, props } from 'kea'
import { loaders } from 'kea-loaders'
import posthog from 'posthog-js'

import api from 'lib/api'
import { lemonToast } from 'lib/lemon-ui/LemonToast/LemonToast'

import { isSharedView } from '~/exporter/exporterViewLogic'
import { QuickFilterContext } from '~/queries/schema/schema-general'
import { QuickFilter } from '~/types'

import { QuickFiltersEvents } from './consts'

export interface QuickFiltersLogicProps {
    context: QuickFilterContext
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface quickFiltersLogicValues {
    quickFilters: QuickFilter[]
    quickFiltersLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface quickFiltersLogicActions {
    createFilter: (payload: Pick<QuickFilter, 'name' | 'options' | 'property_name' | 'type'>) => {
        payload: Pick<QuickFilter, 'name' | 'options' | 'property_name' | 'type'>
    }
    createFilterFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    createFilterSuccess: (
        quickFilters: QuickFilter[],
        payload?: {
            payload: Pick<QuickFilter, 'name' | 'options' | 'property_name' | 'type'>
        }
    ) => {
        quickFilters: QuickFilter[]
        payload?: {
            payload: Pick<QuickFilter, 'name' | 'options' | 'property_name' | 'type'>
        }
    }
    deleteFilter: (id: string) => {
        id: string
    }
    deleteFilterFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    deleteFilterSuccess: (
        quickFilters: QuickFilter[],
        payload?: {
            id: string
        }
    ) => {
        quickFilters: QuickFilter[]
        payload?: {
            id: string
        }
    }
    filterUpdated: (filter: QuickFilter) => {
        filter: QuickFilter
    }
    loadQuickFilters: () => any
    loadQuickFiltersFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadQuickFiltersSuccess: (
        quickFilters: QuickFilter[],
        payload?: any
    ) => {
        quickFilters: QuickFilter[]
        payload?: any
    }
    updateFilter: (
        id: string,
        payload: Partial<Pick<QuickFilter, 'name' | 'options' | 'property_name' | 'type'>>
    ) => {
        id: string
        payload: Partial<Pick<QuickFilter, 'name' | 'options' | 'property_name' | 'type'>>
    }
    updateFilterFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateFilterSuccess: (
        quickFilters: QuickFilter[],
        payload?: {
            id: string
            payload: Partial<Pick<QuickFilter, 'name' | 'options' | 'property_name' | 'type'>>
        }
    ) => {
        quickFilters: QuickFilter[]
        payload?: {
            id: string
            payload: Partial<Pick<QuickFilter, 'name' | 'options' | 'property_name' | 'type'>>
        }
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface quickFiltersLogicMeta {
    key: QuickFilterContext
}

export type quickFiltersLogicType = MakeLogicType<
    quickFiltersLogicValues,
    quickFiltersLogicActions,
    QuickFiltersLogicProps,
    quickFiltersLogicMeta
>

export const quickFiltersLogic = kea<quickFiltersLogicType>([
    path(['lib', 'components', 'QuickFilters', 'quickFiltersLogic']),
    props({} as QuickFiltersLogicProps),
    key((props) => props.context),

    actions({
        createFilter: (payload: Pick<QuickFilter, 'name' | 'property_name' | 'type' | 'options'>) => ({
            payload,
        }),
        updateFilter: (
            id: string,
            payload: Partial<Pick<QuickFilter, 'name' | 'property_name' | 'type' | 'options'>>
        ) => ({ id, payload }),
        deleteFilter: (id: string) => ({ id }),
        filterUpdated: (filter: QuickFilter) => ({ filter }),
    }),

    loaders(({ values, props, actions }) => ({
        quickFilters: [
            [] as QuickFilter[],
            {
                loadQuickFilters: async () => {
                    if (isSharedView()) {
                        return []
                    }
                    const response = await api.quickFilters.list(props.context)
                    return response.results
                },
                createFilter: async ({ payload }) => {
                    const newFilter = await api.quickFilters.create({
                        ...payload,
                        contexts: [props.context],
                    })

                    lemonToast.success('Quick filter created successfully')
                    posthog.capture(QuickFiltersEvents.QuickFilterCreated, {
                        name: payload.name,
                        property_name: payload.property_name,
                        type: payload.type,
                        options: payload.options,
                        context: props.context,
                    })
                    return [newFilter, ...values.quickFilters]
                },
                updateFilter: async ({ id, payload }) => {
                    const updatedFilter = await api.quickFilters.update(id, payload)
                    lemonToast.success('Quick filter updated successfully')
                    posthog.capture(QuickFiltersEvents.QuickFilterUpdated, {
                        ...(payload.name && { name: payload.name }),
                        ...(payload.property_name && { property_name: payload.property_name }),
                        ...(payload.type && { type: payload.type }),
                        ...(payload.options && { options: payload.options }),
                        context: props.context,
                    })
                    actions.filterUpdated(updatedFilter)
                    return values.quickFilters.map((f: QuickFilter) => (f.id === id ? updatedFilter : f))
                },
                deleteFilter: async ({ id }) => {
                    await api.quickFilters.delete(id)
                    lemonToast.success('Quick filter deleted successfully')
                    return values.quickFilters.filter((f: QuickFilter) => f.id !== id)
                },
            },
        ],
    })),

    afterMount(({ actions }) => {
        actions.loadQuickFilters()
    }),
])
